export const latencyEventNames = [
  'user-send',
  'memory-read-start',
  'memory-retrieval-ready',
  'llm-request-start',
  'llm-first-token',
  'llm-generation-finish',
  'speech-text-ready',
  'translation-start',
  'translation-finish',
  'tts-request-start',
  'sidecar-ready',
  'voice-weights-ready',
  'audio-ready',
  'playback-start',
  'playback-end',
] as const

export type LatencyEventName = typeof latencyEventNames[number]

export type SpeechTextSource = 'existing-japanese' | 'second-translation' | 'none'

export type VoiceStart = 'cold' | 'warm' | 'voice-switch'

export type SpeechOutcome = 'played' | 'degraded' | 'skipped'

export const latencyMetricNames = [
  'memoryLatencyMs',
  'llmTtftMs',
  'llmGenerationMs',
  'textResponseLatencyMs',
  'speechPreparationMs',
  'translationLatencyMs',
  'ttsLoadLatencyMs',
  'voiceSwitchLatencyMs',
  'ttsSynthesisLatencyMs',
  'firstAudioLatencyMs',
  'endToEndPlaybackMs',
] as const

export type LatencyMetricName = typeof latencyMetricNames[number]

export interface LatencyEvent {
  name: LatencyEventName
  at: number
}

/**
 * One chat turn's clocks and reasons.
 * The record has no message text, memory text, or credentials.
 */
export interface LatencyTurn {
  turnId: string
  events: LatencyEvent[]
  speechTextSource?: SpeechTextSource
  voiceStart?: VoiceStart
  speechOutcome?: SpeechOutcome
  /** True when synthesis skipped both process start and a weight switch. */
  warmPath?: boolean
  /** Service durations. Present only when that call measured them. */
  measured?: {
    ttsLoadMs?: number
    voiceSwitchMs?: number
    synthesisMs?: number
  }
  metrics: Partial<Record<LatencyMetricName, number>>
  gaps: string[]
}

const latestPlaybackEnd: LatencyEventName = 'playback-end'

function eventAt(turn: LatencyTurn, name: LatencyEventName, which: 'earliest' | 'latest' = 'earliest') {
  const matches = turn.events.filter(event => event.name === name)
  if (matches.length === 0)
    return undefined
  return which === 'latest'
    ? Math.max(...matches.map(event => event.at))
    : Math.min(...matches.map(event => event.at))
}

function between(turn: LatencyTurn, start: LatencyEventName, end: LatencyEventName, endWhich: 'earliest' | 'latest' = 'earliest') {
  const started = eventAt(turn, start)
  const ended = eventAt(turn, end, endWhich)
  if (started === undefined || ended === undefined || ended < started)
    return undefined
  return ended - started
}

/**
 * Fills derived durations from the clocks already stored on one turn.
 * A missing clock stays out of `metrics` and is named in `gaps`.
 *
 * @example
 * deriveLatencyTurn({ turnId: 't1', events: [{ name: 'user-send', at: 0 }, { name: 'llm-request-start', at: 10 }, { name: 'llm-first-token', at: 40 }, { name: 'llm-generation-finish', at: 90 }], metrics: {}, gaps: [] })
 * // => metrics.llmTtftMs === 30
 */
export function deriveLatencyTurn(turn: LatencyTurn): LatencyTurn {
  const metrics: LatencyTurn['metrics'] = {}
  const memory = between(turn, 'memory-read-start', 'memory-retrieval-ready')
  const ttft = between(turn, 'llm-request-start', 'llm-first-token')
  const generation = between(turn, 'llm-request-start', 'llm-generation-finish')
  const text = between(turn, 'user-send', 'llm-generation-finish')
  const speechPrep = between(turn, 'llm-generation-finish', 'speech-text-ready')
  const translation = between(turn, 'translation-start', 'translation-finish')
  const firstAudio = between(turn, 'user-send', 'playback-start')
  const endToEnd = between(turn, 'user-send', 'playback-end', 'latest')
  if (memory !== undefined)
    metrics.memoryLatencyMs = memory
  if (ttft !== undefined)
    metrics.llmTtftMs = ttft
  if (generation !== undefined)
    metrics.llmGenerationMs = generation
  if (text !== undefined)
    metrics.textResponseLatencyMs = text
  if (speechPrep !== undefined)
    metrics.speechPreparationMs = speechPrep
  if (translation !== undefined)
    metrics.translationLatencyMs = translation
  if (turn.voiceStart === 'cold' && turn.measured?.ttsLoadMs !== undefined)
    metrics.ttsLoadLatencyMs = turn.measured.ttsLoadMs
  if (turn.measured?.voiceSwitchMs !== undefined && turn.measured.voiceSwitchMs > 0)
    metrics.voiceSwitchLatencyMs = turn.measured.voiceSwitchMs
  if (turn.measured?.synthesisMs !== undefined)
    metrics.ttsSynthesisLatencyMs = turn.measured.synthesisMs
  if (firstAudio !== undefined)
    metrics.firstAudioLatencyMs = firstAudio
  if (endToEnd !== undefined)
    metrics.endToEndPlaybackMs = endToEnd

  const gaps: string[] = []
  if (eventAt(turn, 'user-send') !== undefined) {
    if (memory === undefined)
      gaps.push('memory')
    if (ttft === undefined)
      gaps.push('llm-ttft')
    if (generation === undefined)
      gaps.push('llm-generation')
  }
  if (turn.speechTextSource === 'second-translation' && translation === undefined)
    gaps.push('translation')
  if (turn.speechOutcome === 'skipped')
    return { ...turn, metrics, gaps }
  if (turn.speechTextSource && turn.speechTextSource !== 'none' && eventAt(turn, 'playback-start') === undefined)
    gaps.push('playback-start')
  if (eventAt(turn, 'playback-start') !== undefined && eventAt(turn, latestPlaybackEnd) === undefined)
    gaps.push('playback-end')
  if (eventAt(turn, 'tts-request-start') !== undefined && !turn.voiceStart)
    gaps.push('voice-start')
  if (turn.voiceStart === 'cold' && metrics.ttsLoadLatencyMs === undefined)
    gaps.push('tts-load')
  if (turn.voiceStart === 'voice-switch' && metrics.voiceSwitchLatencyMs === undefined)
    gaps.push('voice-switch')
  if (eventAt(turn, 'audio-ready') !== undefined && metrics.ttsSynthesisLatencyMs === undefined)
    gaps.push('tts-synthesis')
  return { ...turn, metrics, gaps }
}

/** Inserts one clock. Playback end keeps the latest time. Other names keep the earliest time. */
export function addLatencyEvent(turn: LatencyTurn, event: LatencyEvent): LatencyTurn {
  const next = turn.events.filter(item => item.name !== event.name)
  const previous = turn.events.find(item => item.name === event.name)
  if (!previous)
    next.push(event)
  else if (event.name === 'playback-end')
    next.push(event.at >= previous.at ? event : previous)
  else
    next.push(event.at <= previous.at ? event : previous)
  return deriveLatencyTurn({ ...turn, events: next })
}

export function emptyLatencyTurn(turnId: string): LatencyTurn {
  return deriveLatencyTurn({
    turnId,
    events: [],
    metrics: {},
    gaps: [],
  })
}
